import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { EditLine } from "@dropbox/dig-icons/dist/mjs/assets";
import {
  getWorkflowAgent,
  removeWorkflowAgentLocal,
  setWorkflowAgent,
} from "@mirage/service-conversation";
import { useWorkflowAgents } from "@mirage/service-conversation/hooks/useWorkflowAgents";
import { tagged } from "@mirage/service-logging";
import { FLIPOrchestratorProvider } from "@mirage/shared/FLIP/useFLIPOrchestrator";
import { VerticalExpandable } from "@mirage/shared/FLIP/VerticalExpandable";
import { memo, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { getWorkflowSteps } from "./constants";
import { useSourcesCache } from "./SourcesCacheContext";
import { WorkflowStepCard } from "./WorkflowStepCard";

import { ConversationAssistantPageContent } from "@mirage/mosaics/ConversationAssistant/containers/ConversationAssistantPageContent";
import { useWorkflowAgentAddedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import type {
  AgentId,
  WorkflowAgent,
  WorkflowStepValuesType,
  WorkflowValues,
} from "@mirage/service-conversation/types";
import i18n from "@mirage/translations";
import type { WorkflowOption, WorkflowStepInputType } from "./types";
import styles from "./WorkflowAgent.module.css";

const logger = tagged("WorkflowAgent");

/**
 * Converts a workflow step input into its appropriate value format
 *
 * This function extracts the default/initial value from different input types:
 * - For selection inputs: returns the selected option ID or empty string
 * - For boolean inputs: returns false as default
 * - For multi-select inputs: returns empty array
 * - For combo inputs: recursively processes nested inputs
 * - For other inputs: returns empty string
 *
 * @param input - The workflow step input configuration object
 * @returns The appropriate default value based on input type
 */
export const processInputForDefaultValue = (
  input: WorkflowStepInputType,
): string | boolean | string[] | WorkflowStepValuesType => {
  if (
    input.type === "select" ||
    input.type === "radio-select" ||
    input.type === "tile-select"
  ) {
    // For selection inputs, find the option marked as selected or return empty string
    return (
      input.availableOptions?.find(
        (option: WorkflowOption) => option.isSelected,
      )?.id || ""
    );
  } else if (input.type === "model-select") {
    return "default";
  } else if (input.type === "boolean") {
    return input.isSelected || false;
  } else if (input.type === "icon-select") {
    // Icon select is optional
    return "😄";
  } else if (input.type === "color-select") {
    return "0";
  } else if (input.type === "multi-select" || input.type === "source-select") {
    // Multi-select inputs start with empty selection array
    return [];
  } else if (input.type === "combo-step") {
    // For combo inputs, recursively process each nested input
    const comboValues: WorkflowStepValuesType = {};
    input.comboInputs?.forEach((comboInput: WorkflowStepInputType) => {
      comboValues[comboInput.id] = processInputForDefaultValue(comboInput);
    });
    return comboValues;
  } else {
    // Default case for text inputs and others - empty string
    return "";
  }
};

const getDefaultWorkflowValues = (): WorkflowValues => {
  const defaultValues: WorkflowValues = {};

  getWorkflowSteps().forEach((step) => {
    const stepValues: WorkflowStepValuesType = {};
    step.inputs.forEach((input) => {
      stepValues[input.id] = processInputForDefaultValue(input);
    });
    defaultValues[step.step] = stepValues;
  });

  return defaultValues;
};

export interface WorkflowAgentUIProps {
  agentId?: string;
  onExitWorkflowAgent: () => void;
}
export const WorkflowAgentUI = memo(
  ({ agentId, onExitWorkflowAgent }: WorkflowAgentUIProps) => {
    const workflowSteps = getWorkflowSteps();
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [expandedStep, setExpandedStep] = useState<number | undefined>(
      currentStep,
    );
    const [maxStep, setMaxStep] = useState<number>(0);
    const [selectedTool, setSelectedTool] = useState<AgentId>(
      () => getDefaultWorkflowValues()[1].type as AgentId,
    );
    const [agent, setAgent] = useState<WorkflowAgent | undefined>(undefined);
    const [previewWorkflowAgent, setPreviewWorkflowAgent] = useState<
      WorkflowAgent | undefined
    >(undefined);
    const [workflowValues, setWorkflowValues] = useState<WorkflowValues>(
      getDefaultWorkflowValues,
    );
    const { createAgent } = useWorkflowAgents();
    const { setWorkflowAgentAddedSources, setWorkflowAgentAddedSourcesUUIDs } =
      useWorkflowAgentAddedSources();
    const { getSourcesByIds } = useSourcesCache();
    useEffect(() => {
      async function loadAgent() {
        if (agentId && agentId !== "new") {
          const agent = await getWorkflowAgent(agentId);
          setAgent(agent);
          setWorkflowValues(agent.workflow);
          setCurrentStep(workflowSteps.length);
          setExpandedStep(workflowSteps.length);
        }
      }
      loadAgent();
    }, [agentId]);

    useEffect(() => {
      setMaxStep(Math.max(currentStep, maxStep));
    }, [currentStep, maxStep]);

    const handleStepSubmit = async (
      step: number,
      values: WorkflowStepValuesType,
    ) => {
      logger.debug("Step submission", { step, values });
      setWorkflowValues((prev) => ({
        ...prev,
        [step]: values,
      }));

      if (step < workflowSteps.length) {
        setCurrentStep(step + 1);
        setExpandedStep(step + 1);
      } else if (step === workflowSteps.length) {
        const sourceContents = await getSourcesByIds(
          (workflowValues[4].sources ?? []) as string[],
        );
        const agent = await setWorkflowAgent(
          agentId !== "new" && agentId ? agentId : uuid(),
          {
            ...workflowValues,
            [step]: values,
          },
          sourceContents,
        );
        await createAgent(agent);

        handleExitWorkflowAgent();
      }
    };

    const handlePreview = async (
      step: number,
      values: WorkflowStepValuesType,
    ) => {
      if (workflowValues[step] === values) {
        return;
      }

      setWorkflowValues((prev) => ({
        ...prev,
        [step]: values,
      }));

      const previewAgent = await setWorkflowAgent("preview", {
        ...workflowValues,
        [step]: values,
      });
      setPreviewWorkflowAgent(previewAgent);
    };

    // Update effect to scroll preview container
    useEffect(() => {
      if (previewWorkflowAgent && maxStep === workflowSteps.length) {
        setTimeout(() => {
          containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }, 500);
      }
    }, [previewWorkflowAgent, maxStep]);

    const handleStepBack = () => {
      setCurrentStep((prev) => (prev > 1 ? prev - 1 : prev));
      setExpandedStep((prev) => (prev && prev > 1 ? prev - 1 : prev));
    };

    const handleExitWorkflowAgent = () => {
      // Clear added sources
      setPreviewWorkflowAgent(undefined);
      setMaxStep(0);
      removeWorkflowAgentLocal("preview");
      setWorkflowAgentAddedSources([]);
      setWorkflowAgentAddedSourcesUUIDs([]);
      onExitWorkflowAgent();
    };

    return (
      <FLIPOrchestratorProvider>
        <div className={styles.WorkflowAgentContainer} ref={containerRef}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            marginTop="Micro Medium"
            paddingBottom="Macro Medium"
            style={{
              gap: "var(--dig-spacing__micro__medium)",
              width: "min(528px, 80%)",
            }}
            className={styles.WorkflowStepsContainer}
            as={VerticalExpandable}
          >
            {agentId && agentId !== "new" && (
              <Split
                alignX="left"
                alignY="center"
                width="stretch"
                gap="Micro Medium"
              >
                <Split.Item paddingTop="6">
                  <Text>
                    <UIIcon src={EditLine} />
                  </Text>
                </Split.Item>
                <Split.Item>
                  <Title size="small">
                    {i18n.t("assistant_ai_tool_editing_agent", {
                      agentName: agent?.name,
                    })}
                  </Title>
                </Split.Item>
              </Split>
            )}
            {workflowSteps.map((stepDef) => (
              <WorkflowStepCard
                key={stepDef.step}
                stepDef={stepDef}
                currentStep={currentStep}
                expandedStep={expandedStep}
                selectedTool={selectedTool}
                workflowValues={workflowValues}
                agentId={agentId}
                onExpandedStepChange={setExpandedStep}
                onSelectedToolChange={setSelectedTool}
                onStepSubmit={handleStepSubmit}
                onStepBack={handleStepBack}
                onExitWorkflowAgent={handleExitWorkflowAgent}
                onStepPreview={handlePreview}
              />
            ))}
          </Box>
          {previewWorkflowAgent && maxStep === workflowSteps.length && (
            <div className={styles.WorkflowAgentPreviewContainer}>
              <ConversationAssistantPageContent
                variant="workflow-agent-preview"
                onCloseWorkflowAgentPreview={() => {
                  setPreviewWorkflowAgent(undefined);
                  setMaxStep(0);
                }}
                previewWorkflowAgent={previewWorkflowAgent}
              />
            </div>
          )}
        </div>
      </FLIPOrchestratorProvider>
    );
  },
);

WorkflowAgentUI.displayName = "WorkflowAgentUI";
